/* @flow */
import type { SOM, UsageUnit, UserModel } from './types'
import type { State } from '../../store/store'

/**
 * Commonly used selectors related to users
 * @module 'ducks/user/selectors'
 */

/**
 * Get auth token for current user
 * @param {object} state - Redux store/state
 * @returns {[string]} - Current user auth token
 */
export const userAuthTokenSelector = (state: State): string =>
  state.user.META.currentUser && state.user.META.currentUser.access_token

/**
 * Get id for current user
 * @param {object} state - Redux store/state
 * @returns {[string]} - Current user id
 */
export const userIdSelector = (state: State): ?string =>
  state.user &&
  state.user.META &&
  state.user.META.currentUser &&
  state.user.META.currentUser.user_id
    ? state.user.META.currentUser.user_id
    : null

/**
 * Get current user
 * @param {object} state - Redux store/state
 * @param {string} [validProp=created] - Property used to determine if the user exist
 * @returns {[object]} - Current user
 */
export const currentUserSelector = (
  state: State,
  validProp: string = 'created'
): ?UserModel =>
  userIdSelector(state) &&
  state.user[userIdSelector(state)] &&
  state.user[userIdSelector(state)][validProp]
    ? state.user[userIdSelector(state)]
    : null

/**
 * Get system of measurement for current user
 * @param {Object} state - Current redux state
 * @returns {null|'IMPERIAL'|'METRIC'} - Current user's system of measurement (if available)
 */
export const SOMSelector = (state: State): ?SOM => {
  const user = currentUserSelector(state, 'preference')
  const pref = user && user.preference.find(pref => pref.name === 'IS_METRIC')

  if (pref) {
    return pref.value ? 'METRIC' : 'IMPERIAL'
  } else {
    return null
  }
}

/**
 * Get requested usage units for current user
 * @param {Object} state - Current redux state
 * @returns {null|'TIME'|'ESTIMATED_VOLUME'} - Current user's requested usage units (if available)
 */
export const usageUnitSelector = (state: State): ?UsageUnit => {
  const user = currentUserSelector(state, 'preference')
  const pref =
    user && user.preference.find(pref => pref.name === 'IS_TIME_USAGE')

  if (pref) {
    return pref.value ? 'TIME' : 'ESTIMATED_VOLUME'
  } else {
    return null
  }
}

/**
 * Get if masking as user
 * @param {object} state - Redux store/state
 * @returns {boolean} - if masking as user
 */
export const isMaskingAsUserSelector = (state: State): ?string => {
  return state.user && state.user.isMaskingAsUser
}

/**
 * Get if role is ADMIN or AUDITOR
 * @param {object} state - Redux store/state
 * @returns {boolean} - if role contains ADMIN or AUDITOR
 */
export const isAdminOrAuditorSelector = (state: State): ?string => {
  const userId = userIdSelector(state)
  const auditorOrAdminRoles = ['AUDITOR', 'ADMIN']
  return (
    state.user &&
    state.user[userId] &&
    state.user[userId].role &&
    state.user[userId].role.some(item => auditorOrAdminRoles.includes(item))
  )
}